import {
  parseCompositeKey,
  categoryKeyToMessageContent,
} from "../../../flatbuffers/types/ln2MessageOptions";
import type { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";

export interface CategoryMapping {
  ln2MessageKey: string;
  contentType: "bit" | "byte";
  byte_offset: number;
  offset: number;
  floors_based: boolean;
  is_pair_secondary: boolean;
}

/** Where a mapping's register starts. `bit` rows carry the byte in a different
 *  field from `byte` rows, and reading the wrong one silently yields 0.
 *
 *  Exported so `categoryOccupancy` places each member on the same arithmetic
 *  `spanOf` measures the category's extent with. A second, private copy of this
 *  rule would let the two disagree, and an occupancy map that disagrees with the
 *  extent it refines is worse than no occupancy map at all. */
export function baseOffsetOf(m: CategoryMapping): number {
  return m.contentType === "bit" ? m.byte_offset : m.offset;
}

/**
 * How many bytes ONE mapping occupies.
 *
 * Floor expansion widens a BitMessage and only a BitMessage. The CDO builder is
 * explicit about it: `generateFloorKeys` derives `currentByte = wireBit / 8` per
 * floor for `OffsetContentBitMessage`, so a bit-encoded floor mask genuinely
 * spans `ceil(floors / 8)` bytes — but for `OffsetContentByteMessage` it emits
 * `Offset: offset` unchanged on every floor and varies only `Data`/`Position`.
 * A ByteMessage floor expansion moves the VALUE in one register, not the
 * register.
 *
 * Ignoring that here inflated every byte-encoded floor-based row by the floor
 * count. `Traffic::P` is the canonical one: a single position register that
 * claimed `ceil(12 / 8) = 2` bytes on a 12-floor bank, so it appeared to run
 * into whatever sat in the byte above it. `computeRegCount` in
 * BankOffsetMatrix already stated this rule ("ByteMessage floor expansion only
 * changes the value (not the offset), so it always uses 1 register") — this is
 * the same rule, applied where the span is actually measured.
 */
export function widthOf(m: CategoryMapping, floors: number): number {
  if (m.contentType === "byte") return 1;
  return m.floors_based && floors > 0 ? Math.ceil(floors / 8) : 1;
}

/**
 * Bytes spanned by a set of mappings, measured from the first register to the
 * end of the last — a length, which is what every caller adds to a start offset.
 *
 * That distinction is the whole point. This previously returned
 * `max(baseOffset + width)`, an ABSOLUTE end position, while every consumer
 * treats the result as a length and computes `start + size`. Each column's range
 * was therefore inflated by its own start offset: a register at byte 8, in a
 * category whose furthest member ends at 86, claimed bytes 8-93 instead of the
 * one or two it occupies — so the matrix reported overlaps between registers
 * that never touch.
 */
function spanOf(selected: CategoryMapping[], floors: number): number {
  if (selected.length === 0) return 0;

  let lo = Number.POSITIVE_INFINITY;
  let hi = 0;
  selected.forEach((m) => {
    const base = baseOffsetOf(m);
    if (base < lo) lo = base;
    const end = base + widthOf(m, floors);
    if (end > hi) hi = end;
  });

  return hi - lo;
}

/** Mappings belonging to a category, ignoring the secondary half of a pair —
 *  it shares its primary's range and so cannot widen the span. */
export function selectCategory(
  mappings: ReadonlyArray<CategoryMapping>,
  mc: MessageContent | number,
): CategoryMapping[] {
  return mappings.filter((m) => {
    if (!m.ln2MessageKey || m.is_pair_secondary) return false;
    const parsed = parseCompositeKey(m.ln2MessageKey);
    if (!parsed) return false;
    return categoryKeyToMessageContent(parsed.categoryKey) === mc;
  });
}

/** Byte-size occupied by a category's mappings, including floor-based bitmask
 *  expansion. Extracted from CdoLn1MappingEditor.tsx:277 (computeCategoryByteSize)
 *  so AdapterOffsetMatrix and CdoLn1MappingEditor share one implementation. */
export default function computeCategoryByteSize(
  mappings: ReadonlyArray<CategoryMapping>,
  mc: MessageContent | number,
  floors: number,
): number {
  return spanOf(selectCategory(mappings, mc), floors);
}

/**
 * Byte-size occupied by ONE matrix column.
 *
 * A column is either a whole category (`"control"`) or a single broken-out
 * message (`"control:normal:14"`), and the two carry their own offsets in the
 * matrix — so they need their own widths. Sizing every column by its category
 * made a one-byte register inherit the extent of the furthest member of that
 * category: on a Swift CEC, Independent Service at byte 85 set the width for
 * Energize Up Hall Call at byte 8, which then appeared to collide with
 * everything in between.
 *
 * Falls back to the category span when the key names no mapping of its own,
 * which is the category-column case and the behaviour that was already correct.
 */
export function computeColumnByteSize(
  mappings: ReadonlyArray<CategoryMapping>,
  columnKey: string,
  mc: MessageContent | number,
  floors: number,
): number {
  const own = mappings.filter((m) => !m.is_pair_secondary && m.ln2MessageKey === columnKey);
  if (own.length > 0) return spanOf(own, floors);
  return computeCategoryByteSize(mappings, mc, floors);
}
